import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/common/SiteHeader';
import authService from '../services/auth';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const emailParam = searchParams.get('email');

    if (errorParam) {
      switch (errorParam) {
        case 'AccountExists':
          setError(`An account with email ${emailParam ? decodeURIComponent(emailParam) : 'this email'} already exists. Please sign in instead.`);
          if (emailParam) setEmail(decodeURIComponent(emailParam));
          break;
        case 'GoogleAuthFailed':
          setError('Google authentication failed. Please try again.');
          break;
        case 'InvalidGoogleData':
          setError('Invalid data received from Google. Please try again.');
          break;
        case 'SessionExpired':
          setError('Your session has expired. Please sign in again.');
          break;
        case 'NoToken':
          setError('Authentication failed. Please try again.');
          break;
        default:
          setError('An error occurred during authentication.');
      }
    }

    const message = searchParams.get('message');
    if (message === 'LoggedOut') {
      setSuccess(true);
      setError('');
    }
  }, [searchParams]);

  function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Both fields are required.');
      return;
    }

    // Real login implementation
    authService.login(email, password)
      .then(result => {
        if (result.success) {
          setSuccess(true);
          setTimeout(() => {
            // Use the auth service's redirect logic
            authService.handleLoginSuccess();
          }, 1000);
        } else {
          setError(result.error || 'Login failed. Please try again.');
        }
      })
      .catch(error => {
        setError('Login failed. Please try again.');
      });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
      <SiteHeader />
      <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto bg-white rounded shadow p-8 mt-12" autoComplete="off">
        <h1 className="text-2xl font-bold mb-6">Login</h1>
        <button
          type="button"
          className="w-full flex items-center justify-center gap-2 mb-4 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          onClick={() => authService.initiateGoogleLogin()}
        >
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
          <span className="font-medium text-gray-700">Sign in with Google</span>
        </button>
        <div className="flex items-center my-4">
          <div className="flex-grow h-px bg-gray-200" />
          <span className="mx-2 text-gray-400 text-xs">or</span>
          <div className="flex-grow h-px bg-gray-200" />
        </div>
        {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}
        {success && <div className="mb-4 text-green-600 text-sm">
          {searchParams.get('message') === 'LoggedOut' ? 'Successfully logged out.' : 'Login successful! Redirecting...'}
        </div>}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full border rounded px-3 py-2" />
        </div>
        <div className="mb-6">
          <label className="block mb-1 font-medium">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full border rounded px-3 py-2 pr-10"
              autoComplete="current-password"
            />
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary-500 hover:underline"
              onClick={() => setShowPassword(v => !v)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <button type="submit" className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            Login
          </button>
          <Link to="/register" className="text-primary-500 hover:underline">Don't have an account? Register</Link>
        </div>
      </form>
    </div>
  );
}

export default LoginPage;
